import { gql, useMutation, useQuery } from '@apollo/client';
import { Badge, Callout, Spinner, Button, Flex, TextField, Select } from '@radix-ui/themes';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import AddGroupMember from './elements/AddGroupMembers';
import CreateAccessGroup from './elements/CreateAccessGroup';
import DeleteAccessGroup from './elements/DeleteAccessGroup';
import RemoveUserAccessGroup from './elements/RemoveUserAccessGroup';
import {
  ADD_TO_ACCESS_GROUP,
  CREATE_ACCESS_GROUP,
  DELETE_ACCESS_GROUP,
  REMOVE_USER_FROM_ACCESS_GROUP,
} from './queries';
import { toast } from 'sonner';

const QUERY_CLIENT_ACCESSGROUPS = gql`
  query ACCESSGROUPS {
    accessGroups {
      id
      name
      description
      jobCount
      members {
        id
        firstName
        lastName
        email
        flag
        client {
          id
          orgName
        }
      }
    }
    members: viewer {
      client {
        members {
          totalCount
          nodes {
            id
            firstName
            lastName
            flag
          }
        }
      }
    }
  }
`;

const NamePill = ({ firstName, lastName }: { firstName: string; lastName: string }) => {
  return (
    <span className={'name-pill purple'}>
      {firstName.toUpperCase().slice(0, 1) + lastName.toUpperCase().slice(0, 1)}
    </span>
  );
};

const AccessGroupMember = ({
  member,
  accessGroup,
  removeUser,
  removeUserLoading,
}: {
  member: { id: string; firstName: string; lastName: string; flag: string };
  accessGroup: {
    id: string;
    name: string;
  };
  removeUser: (agId: string, userId: string) => Promise<void>;
  removeUserLoading: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="border border-gray-200 border-t-0 mx-1 px-4 py-2 flex justify-between">
      <div className="flex items-center px-4">
        <div className="mr-2 my-0 pr-2">
          <NamePill firstName={member.firstName} lastName={member.lastName} />
        </div>
        <div className="access-group-member-name mr-1">
          {member.firstName} {member.lastName}
        </div>
        <div>
          {member.flag === 'unconfirmed' && (
            <Badge variant="soft" color="amber" radius="large">
              {t('settings.accessGroup.notActivated')}
            </Badge>
          )}
        </div>
      </div>
      <div className={'text-right px-0'}>
        <RemoveUserAccessGroup
          user={member}
          accessGroup={accessGroup}
          onSubmit={removeUser}
          loading={removeUserLoading}
          disable={false}
        />
      </div>
    </div>
  );
};

const AccessGroup = ({
  accessGroup,
  deleteGroup,
  deleteGroupLoading,
  clientUsers,
  addUsers,
  addUsersLoading,
  removeUser,
  removeUserLoading,
  isOpen,
  onOpenChange,
}: {
  accessGroup: {
    id: string;
    name: string;
    jobCount: number;
    members: {
      id: string;
      firstName: string;
      lastName: string;
      flag: string;
    }[];
  };
  deleteGroup: (accessGroup: { id: string; name: string }) => Promise<void>;
  deleteGroupLoading: boolean;
  clientUsers: {
    id: string;
    firstName: string;
    lastName: string;
  }[];
  addUsers: (id: string, invites: string[]) => Promise<void>;
  addUsersLoading: boolean;
  removeUser: (agId: string, userId: string) => Promise<void>;
  removeUserLoading: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  const open = isOpen !== undefined ? isOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    setInternalOpen(newOpen);
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  };

  return (
    <Collapsible.Root className="mb-6 text-sm" open={open} onOpenChange={handleOpenChange}>
      <div className="border border-gray-200 mx-1 access-group-title px-4 flex py-2 items-center bg-gray-50 rounded-t">
        <Collapsible.Trigger asChild>
          <button className="flex items-center w-1/4 text-left cursor-pointer hover:bg-gray-100 p-1 -ml-1 rounded transition-colors mr-4">
            {open ? (
              <ChevronUp size={18} className="mr-2 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronDown size={18} className="mr-2 text-gray-500 flex-shrink-0" />
            )}
            <span className="font-medium text-gray-800 truncate">{accessGroup.name}</span>
          </button>
        </Collapsible.Trigger>

        <div className="flex items-center w-1/4">
          {accessGroup.members.length === 1
            ? t('settings.accessGroup.memberCountSingular', { count: accessGroup.members.length })
            : t('settings.accessGroup.memberCountPlural', { count: accessGroup.members.length })}
        </div>
        <div className="flex items-center w-1/4">
          {accessGroup.jobCount === 1
            ? t('settings.accessGroup.jobCountSingular', { count: accessGroup.jobCount })
            : t('settings.accessGroup.jobCountPlural', { count: accessGroup.jobCount })}
        </div>
        <div className="w-1/4 flex flex-row justify-end gap-2">
          <AddGroupMember accessGroup={accessGroup} users={clientUsers} onSubmit={addUsers} loading={addUsersLoading} />
          <DeleteAccessGroup
            accessGroup={accessGroup}
            onSubmit={deleteGroup}
            loading={deleteGroupLoading}
            disable={false}
          />
        </div>
      </div>

      <Collapsible.Content className="data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp overflow-hidden">
        <div className="border-l border-r border-b border-gray-200 mx-1 rounded-b shadow-inner">
          {accessGroup && accessGroup.members.length > 0 ? (
            accessGroup.members.map((user, index) => (
              <AccessGroupMember
                key={index}
                member={user}
                accessGroup={accessGroup}
                removeUser={removeUser}
                removeUserLoading={removeUserLoading}
              />
            ))
          ) : (
            <div className="p-4 text-gray-500 text-center italic border-t-0">No members in this group</div>
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

type SortField = 'name' | 'members' | 'jobs';
type SortOrder = 'asc' | 'desc';

const AccessGroups = () => {
  const { t } = useTranslation();
  const {
    data: clientAccessGroups,
    refetch,
    loading: groupsLoading,
  } = useQuery<{
    accessGroups: {
      id: string;
      name: string;
      jobCount: number;
      members: {
        id: string;
        firstName: string;
        lastName: string;
        flag: string;
      }[];
    }[];
    members: { client: { members: { nodes: { id: string; firstName: string; lastName: string }[] } } };
  }>(QUERY_CLIENT_ACCESSGROUPS);

  const [addUsersToGroup, { loading: addUserLoading }] = useMutation(ADD_TO_ACCESS_GROUP);
  const [createAccessGroup, { loading: createGroupLoading }] = useMutation(CREATE_ACCESS_GROUP);
  const [deleteAccessGroup, { loading: deleteGroupLoading }] = useMutation(DELETE_ACCESS_GROUP);
  const [removeUserGroup, { loading: removeUserGroupLoading }] = useMutation(REMOVE_USER_FROM_ACCESS_GROUP);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const allGroups = clientAccessGroups?.accessGroups || [];
  const allExpanded = useMemo(() => {
    if (allGroups.length === 0) return false;
    return allGroups.every((group) => openGroups[group.id]);
  }, [openGroups, allGroups]);

  const filteredAndSortedGroups = useMemo(() => {
    if (!allGroups) return [];

    let groups = [...allGroups];

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      groups = groups.filter((g) => {
        if ((g.name || '').toLowerCase().includes(lowerQuery)) return true;
        return g.members.some((m) => {
          const fName = (m.firstName || '').toLowerCase();
          const lName = (m.lastName || '').toLowerCase();
          const fullName = `${fName} ${lName}`;
          return fName.includes(lowerQuery) || lName.includes(lowerQuery) || fullName.includes(lowerQuery);
        });
      });
    }

    groups.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      } else if (sortField === 'members') {
        comparison = a.members.length - b.members.length;
      } else if (sortField === 'jobs') {
        comparison = a.jobCount - b.jobCount;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return groups;
  }, [allGroups, searchQuery, sortField, sortOrder]);

  const toggleAll = () => {
    if (allExpanded) {
      setOpenGroups({});
    } else {
      const newOpenGroups: Record<string, boolean> = {};
      allGroups.forEach((group) => {
        newOpenGroups[group.id] = true;
      });
      setOpenGroups(newOpenGroups);
    }
  };

  const handleOpenChange = (id: string, isOpen: boolean) => {
    setOpenGroups((prev) => ({
      ...prev,
      [id]: isOpen,
    }));
  };

  const addUsers = (accessGroupId: string, invitees: string[]) => {
    return addUsersToGroup({
      variables: {
        accessGroup: accessGroupId,
        invitees: invitees,
      },
    }).then(() => {
      refetch();
    });
  };

  const deleteGroup = async (accessGroup: { id: string; name: string }) => {
    await deleteAccessGroup({
      variables: {
        accessGroupId: accessGroup.id,
      },
      update(cache) {
        cache.evict({
          id: cache.identify(accessGroup),
        });
      },
    }).then((_) => {
      toast.success(t('settings.accessGroup.deleteSuccessToast', { groupName: accessGroup.name }));
    });
  };

  const removeUserFromGroup = (accessGroupId: string, userId: string) => {
    return removeUserGroup({
      variables: {
        accessGroupId: accessGroupId,
        userId: userId,
      },
    }).then(() => {
      refetch();
    });
  };

  const handleCreateGroupSubmit = (name: string) => {
    return createAccessGroup({
      variables: {
        name: name,
      },
    }).then(() => {
      refetch();
    });
  };

  return (
    <div className="border border-gray-200 rounded p-4">
      <div className="flex flex-row justify-between align-middle items-center mb-4">
        <h2 className="truncate">{t('settings.accessGroup.heading')}</h2>
        <div className="flex flex-row gap-2">
          <Flex gap="3" align="center">
            <TextField.Root
              placeholder={t('common.search', 'Search...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            >
              <TextField.Slot>
                <Search height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>
            <Select.Root
              value={`${sortField}-${sortOrder}`}
              onValueChange={(val) => {
                const parts = val.split('-');
                setSortField(parts[0] as SortField);
                setSortOrder(parts[1] as SortOrder);
              }}
            >
              <Select.Trigger />
              <Select.Content>
                <Select.Group>
                  <Select.Label>{t('common.sort', 'Sort by')}</Select.Label>
                  <Select.Item value="name-asc">{t('settings.sort.nameAsc', 'Name (A-Z)')}</Select.Item>
                  <Select.Item value="name-desc">{t('settings.sort.nameDesc', 'Name (Z-A)')}</Select.Item>
                  <Select.Item value="members-asc">
                    {t('settings.accessGroup.sort.membersAsc', 'Members (Lowest first)')}
                  </Select.Item>
                  <Select.Item value="members-desc">
                    {t('settings.accessGroup.sort.membersDesc', 'Members (Highest first)')}
                  </Select.Item>
                  <Select.Item value="jobs-asc">
                    {t('settings.accessGroup.sort.jobsAsc', 'Jobs (Lowest first)')}
                  </Select.Item>
                  <Select.Item value="jobs-desc">
                    {t('settings.accessGroup.sort.jobsDesc', 'Jobs (Highest first)')}
                  </Select.Item>
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
          {allGroups.length > 0 && (
            <Button variant="outline" onClick={toggleAll} color="gray">
              {allExpanded ? (
                <>
                  <ChevronsUp size={16} className="mr-1" />
                  {t('settings.accessGroup.collapseAll', 'Collapse All')}
                </>
              ) : (
                <>
                  <ChevronsDown size={16} className="mr-1" />
                  {t('settings.accessGroup.expandAll', 'Expand All')}
                </>
              )}
            </Button>
          )}
          <CreateAccessGroup onSubmit={handleCreateGroupSubmit} loading={createGroupLoading} disable={false} />
        </div>
      </div>
      {clientAccessGroups?.accessGroups.length === 0 && (
        <Callout.Root color="blue">
          <Callout.Text className="mb-0">{t('settings.accessGroup.emptyState')}</Callout.Text>
        </Callout.Root>
      )}
      {clientAccessGroups &&
        filteredAndSortedGroups.map((group, index) => {
          return (
            <AccessGroup
              key={index}
              isOpen={!!openGroups[group.id]}
              onOpenChange={(isOpen) => handleOpenChange(group.id, isOpen)}
              accessGroup={group}
              deleteGroup={deleteGroup}
              deleteGroupLoading={deleteGroupLoading}
              addUsers={addUsers}
              addUsersLoading={addUserLoading}
              removeUser={removeUserFromGroup}
              removeUserLoading={removeUserGroupLoading}
              clientUsers={clientAccessGroups.members.client.members.nodes}
            />
          );
        })}

      {groupsLoading && (
        <div className="flex justify-center">
          <Spinner size="3" role="status" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

export default AccessGroups;
